/**
 * 操控变形对话框（Puppet Warp 计划）。
 *
 * 全屏模态 + 代理分辨率实时预览（`onRequestPreview` 回调返回的预览帧
 * 已经是降采样过的，见 `PuppetWarpController.previewFrame` 的说明）。
 * 点空白新增图钉、点已有图钉选中、拖拽移动，Delete/Backspace 删除选中
 * 图钉——手势判定（`hitTestPin`/`addPin`/`movePin`/`removePin`）全部在
 * 装配层持有的 controller 里，这里只转发指针事件+按键、把返回帧画上去，
 * 跟 `LiquifyDialog.tsx` 一样不持有业务对象（CLAUDE.md 规则 4）。
 */
import type { JSX } from 'solid-js';
import { Show, createEffect, createSignal } from 'solid-js';
import { useFocusTrap } from '../a11y/focus-trap';
import type { Pin, PuppetPreviewFrame } from '../../interaction/tools/puppet-tool';
import styles from './PuppetWarpDialog.module.css';

export interface PuppetWarpDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onAddPin: (x: number, y: number) => number;
  readonly onHitTestPin: (x: number, y: number, toleranceDoc: number) => number | null;
  readonly onMovePin: (id: number, x: number, y: number) => void;
  readonly onRemovePin: (id: number) => void;
  readonly onGetPins: () => readonly Pin[];
  readonly onRequestPreview: () => Promise<PuppetPreviewFrame | null>;
  /** 确定：外面负责调 `PuppetWarpController.commit()`。 */
  readonly onApply: () => Promise<boolean>;
}

/** 文档像素——命中测试容差、图钉标记半径都用它，跟具体图片分辨率无关。 */
const PIN_HIT_TOLERANCE_DOC = 12;
/** 代理分辨率目标长边，`main.tsx` 调用 `controller.previewFrame()` 要用同一个值。 */
export const PROXY_LONG_EDGE = 512;
const PIN_MARKER_RADIUS_PX = 6;

export function PuppetWarpDialog(props: PuppetWarpDialogProps): JSX.Element {
  const [busy, setBusy] = createSignal(false);
  const [pins, setPins] = createSignal<readonly Pin[]>([]);
  const [canvasSize, setCanvasSize] = createSignal({ width: 1, height: 1 });
  let canvasEl: HTMLCanvasElement | undefined;
  let rootEl: HTMLDivElement | undefined;
  let frameInfo: { scale: number; origin: { x: number; y: number } } | null = null;
  let draggingId: number | null = null;
  let selectedId: number | null = null;
  // 焦点交给 useFocusTrap（M5-02）统一管——它会落在对话框内第一个真正
  // 可聚焦的控件（"取消"按钮），比原来手动 `rootEl.focus()` 落在一个
  // tabindex=-1 的 div 上对键盘/读屏用户更友好；`onKeyDown` 挂在
  // `rootEl` 本身，Delete/Backspace 靠事件冒泡照样能收到，不受影响
  useFocusTrap(() => props.open, () => rootEl ?? null, props.onClose);

  createEffect(() => {
    if (props.open) {
      setBusy(false);
      setPins(props.onGetPins());
      frameInfo = null;
      draggingId = null;
      selectedId = null;
      void refreshPreview();
    }
  });

  async function refreshPreview(): Promise<void> {
    const frame = await props.onRequestPreview();
    if (frame === null) return;
    frameInfo = { scale: frame.scale, origin: frame.origin };
    setCanvasSize({ width: frame.rect.width, height: frame.rect.height });
    paintFrame(frame);
  }

  function paintFrame(frame: PuppetPreviewFrame): void {
    const ctx = canvasEl?.getContext('2d');
    if (ctx == null || frame.rect.width <= 0 || frame.rect.height <= 0) return;
    const image = new ImageData(Uint8ClampedArray.from(frame.data), frame.rect.width, frame.rect.height);
    ctx.putImageData(image, 0, 0);
    drawPinMarkers(ctx);
  }

  /** 画布绘制颜色也受 §7.2 token 约束——从 computed style 读，不写字面量
   * （同 `CanvasView.tsx` 棋盘格颜色的既有先例）。 */
  function cssVar(name: string): string {
    const el = rootEl ?? canvasEl;
    return el === undefined ? '' : getComputedStyle(el).getPropertyValue(name).trim();
  }

  function drawPinMarkers(ctx: CanvasRenderingContext2D): void {
    const info = frameInfo;
    if (info === null) return;
    const strokeColor = cssVar('--color-accent-text');
    for (const pin of pins()) {
      const px = (pin.current.x - info.origin.x) * info.scale;
      const py = (pin.current.y - info.origin.y) * info.scale;
      ctx.beginPath();
      ctx.arc(px, py, PIN_MARKER_RADIUS_PX, 0, Math.PI * 2);
      ctx.fillStyle = cssVar(pin.id === selectedId ? '--color-danger' : '--color-accent');
      ctx.fill();
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
  }

  /** 客户端坐标（CSS 像素）→ 文档像素坐标（经代理 scale/origin 换算）。 */
  function toDocPoint(event: PointerEvent): { x: number; y: number } | null {
    const info = frameInfo;
    const canvas = canvasEl;
    if (info === null || canvas == null) return null;
    const rect = canvas.getBoundingClientRect();
    const cssScaleX = canvas.width / rect.width;
    const cssScaleY = canvas.height / rect.height;
    const proxyX = (event.clientX - rect.left) * cssScaleX;
    const proxyY = (event.clientY - rect.top) * cssScaleY;
    return { x: info.origin.x + proxyX / info.scale, y: info.origin.y + proxyY / info.scale };
  }

  function onPointerDown(event: PointerEvent): void {
    if (busy()) return;
    const point = toDocPoint(event);
    if (point === null) return;
    const hit = props.onHitTestPin(point.x, point.y, PIN_HIT_TOLERANCE_DOC);
    if (hit !== null) {
      selectedId = hit;
      draggingId = hit;
    } else {
      const id = props.onAddPin(point.x, point.y);
      selectedId = id;
      draggingId = id;
    }
    canvasEl?.setPointerCapture(event.pointerId);
    setPins(props.onGetPins());
    void refreshPreview();
  }

  function onPointerMove(event: PointerEvent): void {
    if (draggingId === null) return;
    const point = toDocPoint(event);
    if (point === null) return;
    props.onMovePin(draggingId, point.x, point.y);
    setPins(props.onGetPins());
    void refreshPreview();
  }

  function onPointerUp(event: PointerEvent): void {
    if (draggingId === null) return;
    draggingId = null;
    canvasEl?.releasePointerCapture(event.pointerId);
  }

  function onKeyDown(event: KeyboardEvent): void {
    if ((event.key === 'Delete' || event.key === 'Backspace') && selectedId !== null) {
      props.onRemovePin(selectedId);
      selectedId = null;
      setPins(props.onGetPins());
      void refreshPreview();
    }
  }

  return (
    <Show when={props.open}>
      <div class={styles.backdrop} data-testid="puppet-warp-backdrop">
        <div
          ref={(el) => (rootEl = el)}
          class={styles.dialog}
          role="dialog"
          aria-label="操控变形"
          data-testid="puppet-warp-dialog"
          tabIndex={-1}
          onKeyDown={onKeyDown}
        >
          <h2 class={styles.title}>操控变形</h2>

          <div class={styles.canvasWrap}>
            <canvas
              ref={(el) => (canvasEl = el)}
              width={canvasSize().width}
              height={canvasSize().height}
              class={styles.canvas}
              data-testid="puppet-warp-canvas"
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
            />
          </div>

          <p class={styles.hint}>
            点空白处放置图钉，拖动图钉让画面跟着变形；选中图钉后按 Delete/Backspace 删除。
            只对当前活动图层生效，预览为降采样代理，确定后按全分辨率计算。
          </p>

          <div class={styles.actions}>
            <button type="button" class={styles.secondary} onClick={props.onClose}>
              取消
            </button>
            <button
              type="button"
              class={styles.primary}
              data-testid="puppet-warp-apply"
              disabled={busy()}
              onClick={() => {
                setBusy(true);
                void props.onApply().finally(() => setBusy(false));
              }}
            >
              {busy() ? '处理中…' : '确定'}
            </button>
          </div>
        </div>
      </div>
    </Show>
  );
}
